import { isTemplatePlaceholder } from '@/types';
import { EngagementHistoryFilter } from '@/types/searchFilterTypes';
import { formatTemplateCapableDateForFilterValue } from '@/util';
import {
  ACTIVITY_OPTIONS_WITH_IDS,
  ACTIVITY_STATUSES_WITH_IDS,
} from '@/constants/filterCriteria/engagement';
import moment from 'moment';
import { EngagementHistoryWidgetValue } from '@/pages/AdvancedSearchListCreatorPage/customWidgets/EngagementHistoryWidget';
import { checkDateFieldValueIsValid } from './dateFieldValidation';
import type { FilterMapper } from './filterMapperTypes';

export const EngagementHistoryMapper: FilterMapper = {
  checkIsIncomplete({ value }) {
    const { activity, activityDateRange }: EngagementHistoryWidgetValue = value[0];
    const isNoActivitySelected = !isTemplatePlaceholder(activity) && activity.length === 0;

    const isStartDateValid =
      isTemplatePlaceholder(activityDateRange.displayedStartDate) ||
      checkDateFieldValueIsValid(activityDateRange.displayedStartDate);
    const isEndDateValid =
      isTemplatePlaceholder(activityDateRange.displayedEndDate) ||
      checkDateFieldValueIsValid(activityDateRange.displayedEndDate);

    const isStartDateAfterEndDate =
      !isTemplatePlaceholder(activityDateRange.displayedStartDate) &&
      !isTemplatePlaceholder(activityDateRange.displayedEndDate) &&
      moment(activityDateRange.displayedStartDate).isAfter(
        moment(activityDateRange.displayedEndDate)
      );

    return isNoActivitySelected || !isStartDateValid || !isEndDateValid || isStartDateAfterEndDate;
  },

  toFilter({ value }): EngagementHistoryFilter {
    if (!value) throw new Error('Missing value');
    // Why an array of length 1? Thats just the way it is the way we use it
    if (value.length !== 1) throw new Error('Invalid value. Expected array of length 1');

    const { activity, activityDateRange, activityStatus, subject }: EngagementHistoryWidgetValue =
      value[0];

    if (activity === undefined) throw new Error('Missing activity array');

    return {
      name: 'engagementHistory',
      value: {
        activityIds: isTemplatePlaceholder(activity) ? activity : activity.map(({ id }) => id),
        activityStatusIds: isTemplatePlaceholder(activityStatus)
          ? activityStatus
          : activityStatus.map(({ id }) => id),
        activityDateRange: {
          ...activityDateRange,
          displayedStartDate: formatTemplateCapableDateForFilterValue(
            activityDateRange.displayedStartDate,
            isTemplatePlaceholder,
            null
          ),
          displayedEndDate: formatTemplateCapableDateForFilterValue(
            activityDateRange.displayedEndDate,
            isTemplatePlaceholder,
            null
          ),
        },
        subject,
      },
    };
  },

  async fromFilter(filter: EngagementHistoryFilter) {
    const { activityStatusIds, activityDateRange, activityIds, subject } = filter.value;

    const value: EngagementHistoryWidgetValue = {
      activity: isTemplatePlaceholder(activityIds)
        ? activityIds
        : ACTIVITY_OPTIONS_WITH_IDS.filter(({ id }) => activityIds.includes(id)),
      activityDateRange: {
        ...activityDateRange,
        displayedEndDate: isTemplatePlaceholder(activityDateRange.displayedEndDate)
          ? activityDateRange.displayedEndDate
          : activityDateRange.displayedEndDate
            ? new Date(activityDateRange.displayedEndDate)
            : null,
        displayedStartDate: isTemplatePlaceholder(activityDateRange.displayedStartDate)
          ? activityDateRange.displayedStartDate
          : activityDateRange.displayedStartDate
            ? new Date(activityDateRange.displayedStartDate)
            : null,
      },
      activityStatus: isTemplatePlaceholder(activityStatusIds)
        ? activityStatusIds
        : ACTIVITY_STATUSES_WITH_IDS.filter(({ id }) => activityStatusIds.includes(id)),
      subject: subject || '',
      errors: [],
    };
    return {
      field: filter.name,
      operator: 'any in',
      value: [value],
    };
  },
};
